/////////////////////////////////////////////////////
// Add your custom code here.
// This file and any changes you make to it are preserved every time the app is generated.
/////////////////////////////////////////////////////
import { Inject, Injector, Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { progress } from "@progress/jsdo-core";
import { IntlService } from "@progress/kendo-angular-intl";
import { SortDescriptor, orderBy } from "@progress/kendo-data-query";
import { TimeoutManagerService } from "../../../shared/services/timeout-manager.service";
import { CustomAuthorizationService } from "../../../core/auth/custom-authorization.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { GroupServiceDetailsViewBaseComponent } from "./group-service-details.view.base.component";

const ServiceTypes = require("../../../shared/components/djiv-tables/service-types.json");
const DEFAULT_SERVICE_ICON = "fa-address-book-o";

@Component({
    templateUrl: "./group-service-details.view.component.extended.html",
    styleUrls: ["./group-service-details.view.component.css"],
})
export class GroupServiceDetailsViewComponent extends GroupServiceDetailsViewBaseComponent {
    public sortChanges: SortDescriptor[] = [
        { field: "ChangeDate", dir: "desc" },
    ];
    public description: any;
    public prefixDescription: any;

    public ttGroupService: any;
    public ttGroupServiceChanges: any;
    public ttGroupServiceChangeReasons: any;
    public ttGroupServiceDetailAcco: any;
    public ttGroupServiceDetailExtra: any;
    public ttGroupServiceContact: any;

    public groupServiceDetailAccoByUnitType: any;
    public groupServiceDetailExtraByUnitType: any;

    public ttGroupServiceUnit: any;
    public groupServiceUnitTotal: any;

    protected jsdo: progress.data.JSDO;
    protected isLoading: boolean = true;
    protected ServiceInfo: any;

    private timeoutManagerService: TimeoutManagerService;
    private router: Router;
    private session: progress.data.JSDO;

    private ProductCode: string;
    private ServiceType: string;
    private ServiceCode: string;
    private SegmentBeginDate: string;

    public isPrinting: Boolean = false;

    constructor(
        @Inject(Injector) injector: Injector,
        @Inject(ActivatedRoute) protected route: ActivatedRoute,
        @Inject(IntlService) protected intl: IntlService,
        @Inject(CustomAuthorizationService)
        protected customAuthorizationService: CustomAuthorizationService,
        @Inject(DomSanitizer) protected domSanitizer: DomSanitizer,
        @Inject(Location) private location: Location
    ) {
        super(injector);
        this.timeoutManagerService = injector.get(TimeoutManagerService);
        this.router = injector.get(Router);
        this.initData();
        this.readData();
    }

    // Fired when component is initialized and input properties are set
    public onInit(): void {
        // this.initHeaderPanel();
    }

    // Fired when component's views and child views are initialized
    public onShow(): void {}
    // Fired just before Angular destroys the component. Unsubscribe Observables and detach event handlers to avoid memory leaks
    public onHide(): void {}

    private initData() {
        this.route.queryParams.forEach((params) => {
            this.ProductCode = params["ProductCode"];
            this.ServiceType = params["ServiceType"];
            this.ServiceCode = params["ServiceCode"];
            this.SegmentBeginDate = params["SegmentBeginDate"];
        });
        this.ServiceInfo = {};
        this.session = new progress.data.JSDO(
            "GroupServiceDetailBusinessEntity"
        );
        this.session.subscribe("afterFill", (jsdo, success, response) => {
            this.jsdo = jsdo;

            if (success) {
                const dsGroupServiceDetail = Object.assign(
                    {},
                    response.response.dsGroupServiceDetail
                );

                if (this.isGroupServiceEmpty(dsGroupServiceDetail)) {
                    this.router.navigate(["group-service/services"]);
                    return;
                }

                if (dsGroupServiceDetail.ttGroupService)
                    this.ttGroupService = dsGroupServiceDetail.ttGroupService;

                if (dsGroupServiceDetail.ttGroupServiceUnit)
                    this.ttGroupServiceUnit =
                        dsGroupServiceDetail.ttGroupServiceUnit;

                if (dsGroupServiceDetail.ttGroupServiceChanges)
                    this.ttGroupServiceChanges =
                        dsGroupServiceDetail.ttGroupServiceChanges;

                if (dsGroupServiceDetail.ttGroupServiceChangeReasons)
                    this.ttGroupServiceChangeReasons =
                        dsGroupServiceDetail.ttGroupServiceChangeReasons;

                if (dsGroupServiceDetail.ttGroupServiceDetailAcco)
                    this.ttGroupServiceDetailAcco =
                        dsGroupServiceDetail.ttGroupServiceDetailAcco;

                if (dsGroupServiceDetail.ttGroupServiceDetailExtra)
                    this.ttGroupServiceDetailExtra =
                        dsGroupServiceDetail.ttGroupServiceDetailExtra;

                if (dsGroupServiceDetail.ttGroupServiceContact)
                    this.ttGroupServiceContact =
                        dsGroupServiceDetail.ttGroupServiceContact[0];

                Object.assign(this.ServiceInfo, this.ttGroupService[0]);

                this.handleGroupService();
                this.handleGroupServiceUnit();
                this.handleGroupServiceChanges();
                this.handleGroupServiceDetailExtra();
                this.handleGroupServiceDetailAcco();
            } else if (response.response.status_code === 401) {
                this.timeoutManagerService.handleTimeout();
            }

            Promise.all([
                this.customAuthorizationService.processAuthorizationResponse(),
            ]).then(() => {
                this.isLoading = false;
            });
        });
    }

    protected readData() {
        this.isLoading = true;
        return this.session.fill({
            filter: JSON.stringify(this.filterData()),
        });
    }

    private isGroupServiceEmpty(dsGroupServiceDetail: any): boolean {
        return (
            !dsGroupServiceDetail ||
            (this.isArrayNullOrEmpty(dsGroupServiceDetail.ttGroupService) &&
                this.isArrayNullOrEmpty(
                    dsGroupServiceDetail.ttGroupServiceChanges
                ) &&
                this.isArrayNullOrEmpty(
                    dsGroupServiceDetail.ttGroupServiceDetailAcco
                ) &&
                this.isArrayNullOrEmpty(
                    dsGroupServiceDetail.ttGroupServiceDetailExtra
                ))
        );
    }

    private isArrayNullOrEmpty(array: any[]): boolean {
        return array === null || array === undefined || array.length === 0;
    }

    private handleGroupServiceChanges() {
        if (!this.ttGroupServiceChanges) return;

        this.ttGroupServiceChanges.forEach((change) => {
            change.ChangeDate = new Date(change.ChangeDate);
            if (change.ConfirmationDate != null) {
                change.ConfirmationDate = new Date(change.ConfirmationDate);
            }
        });
        this.ttGroupServiceChanges = {
            data: orderBy(this.ttGroupServiceChanges, this.sortChanges),
            total: this.ttGroupServiceChanges.length,
        };
    }

    public async confirmBooking(externalBookRef) {
        try {
            this.jsdo["ttGroupService"].foreach((element) => {
                element.assign({
                    ExternalGroupBookID: externalBookRef,
                });
            });

            this.isLoading = true;
            await this.jsdo.saveChanges(true);
            await this.readData();
        } catch (e) {
            this.isLoading = false;
            if (e.request.response.status_code === 401) {
                this.timeoutManagerService.handleTimeout();
            }
        }
    }

    private splitDetailByUnitDescription(ttGroupServiceDetail): any {
        if (!this.ttGroupServiceUnit)
            return [
                {
                    UnitDescription: "All",
                    DetailRows: ttGroupServiceDetail,
                },
            ];

        let splitArray = [];
        let lastUnitNr;

        this.ttGroupServiceUnit.forEach((unitType) => {
            lastUnitNr = undefined;
            splitArray.push({
                UnitDescription: unitType.UnitDescription,
                DetailRows: ttGroupServiceDetail
                    .filter(
                        (detailRow) =>
                            detailRow.UnitDescription ===
                            unitType.UnitDescription
                    )
                    .sort((a, b) => {
                        if (a.UnitNr < b.UnitNr) return -1;

                        if (a.UnitNr > b.UnitNr) return 1;

                        if (a.UnitNr === b.UnitNr) {
                            if (a.GuestName < b.GuestName) return -1;

                            if (a.GuestName > b.GuestName) return 1;

                            if (a.GuestName === b.GuestName) return 0;
                        }
                    })
                    .map((a, i, arr) => {
                        //add i/arr to check if it's the last check we do; we could probably move lastUnitNr = undefined to line 181/182
                        if (lastUnitNr !== a.UnitNr) {
                            lastUnitNr = a.UnitNr;
                            return a;
                        } else {
                            let copy = { ...a };
                            copy.UnitNr = undefined;
                            return copy;
                        }

                        // if (arr.length - 1 === i) {
                        //     lastUnitNr = undefined;
                        // }
                    }),
            });
        });
        return splitArray;
    }
    private handleGroupServiceDetailExtra() {
        if (!this.ttGroupServiceDetailExtra) return;

        this.groupServiceDetailExtraByUnitType =
            this.splitDetailByUnitDescription(this.ttGroupServiceDetailExtra);
    }

    private handleGroupServiceDetailAcco() {
        if (!this.ttGroupServiceDetailAcco) return;

        this.groupServiceDetailAccoByUnitType =
            this.splitDetailByUnitDescription(this.ttGroupServiceDetailAcco);
    }

    private handleGroupService() {
        if (!this.ttGroupService) return;
        this.ttGroupService = this.ttGroupService[0];
    }

    private handleGroupServiceUnit() {
        if (!this.ttGroupServiceUnit) return;

        let totalUnits: number = 0;
        let totalGuests: number = 0;

        this.ttGroupServiceUnit.forEach((unitType) => {
            totalUnits += unitType.NumUnits;
            totalGuests += unitType.NumGuests;
        });

        this.groupServiceUnitTotal = {
            NumUnits: totalUnits,
            NumGuests: totalGuests,
        };
    }

    private kendoFilterData() {
        return [
            {
                field: "ProductCode",
                operator: "eq",
                value: this.ProductCode,
            },
            {
                field: "ServiceType",
                operator: "eq",
                value: this.ServiceType,
            },
            {
                field: "ServiceCode",
                operator: "eq",
                value: this.ServiceCode,
            },
            {
                field: "SegmentBeginDate",
                operator: "eq",
                value: this.SegmentBeginDate,
            },
        ];
    }

    private filterData() {
        return {
            dsFilter: {
                ttFilter: this.kendoFilterData(),
            },
        };
    }

    public printService() {
        this.isPrinting = false;
        setTimeout(function () {
            window.print();
        }, 500);
    }

    public backButton() {
        this.location.back();
    }

    public getSafeHtmlChanges(changes: string) {
        return this.domSanitizer.bypassSecurityTrustHtml(changes);
    }

    public getChangeReasons(GroupServiceChangesId) {
        return this.ttGroupServiceChangeReasons.filter(
            (el) => el.GroupServiceChangesId === GroupServiceChangesId
        );
    }

    public getGroupServiceStatusIconClass(): string {
        switch (this.ttGroupService.GroupServiceStatus) {
            case "Guaranteed":
                return "coloured fa fa-check";

            case "Planned":
                return "coloured fa fa-tasks";

            case "Cancelled":
                return "coloured fa fa-ban";

            default:
                return "coloured fa fa-question";
        }
    }

    public getIconClass(): string {
        if (this.ttGroupService.ServiceType === "EX")
            return ServiceTypes[this.ttGroupService.ServiceType][
                this.ttGroupService.ServiceType
            ].icon;
        return ServiceTypes[this.ttGroupService.ServiceType]
            ? ServiceTypes[this.ttGroupService.ServiceType].icon
            : DEFAULT_SERVICE_ICON;
    }

    // public async confirmService(externalBookRef) {
    //     try {
    //         this.jsdo["ttGroupService"].foreach((element) => {
    //             element.assign({
    //                 ExternalGroupBookID: externalBookRef,
    //             });
    //         });

    //         this.isLoading = true;
    //         await this.jsdo.saveChanges(true);
    //         await this.readData();
    //     } catch (e) {
    //         this.isLoading = false;
    //         if (e.request.response.status_code === 401) {
    //             this.timeoutManagerService.handleTimeout();
    //         }
    //     }
    // }
}



// WEBPACK FOOTER //
// C:/Repositories/wfa3/SupplierPortal/app/src/app/modules/group-service/group-service-details/group-service-details.view.component.ts